import type { ApiClient } from './client'
import type { components } from '@aurigaengineering/contracts-admin'

export type MockView = components['schemas']['MockView']
export type IntroPageView = components['schemas']['IntroPageView']
export type PreviewLaunch = components['schemas']['PreviewLaunch']
export type MockImportOutcome = components['schemas']['MockImportOutcome']

/** The Mocks screen's calls, behind an interface so a test can hand it a stand-in. */
export interface MocksApi {
  upload(exam: string, file: File, preview: boolean): Promise<MockImportOutcome>
  list(exam: string): Promise<MockView[]>
  publish(code: string): Promise<MockView>
  counting(code: string, towardsDailyLimit: boolean): Promise<MockView>
  /** Throws a Draft away; its Questions go back where they came from. */
  discard(code: string): Promise<void>
  /** A year's intro file onto the Mock: what a Student reads before Question 1 (app:R85). */
  setIntro(code: string, yaml: string): Promise<MockView>
  clearIntro(code: string): Promise<MockView>
  /** Mints a Simulator link for the whole Mock, intro pages first. The API names the address, not us. */
  preview(code: string): Promise<PreviewLaunch>
}

export function mocksApi(client: ApiClient): MocksApi {
  return {
    async upload(exam, file, preview) {
      const form = new FormData()
      form.append('files', file, file.name)
      const { data, error } = await client.POST('/admin/mocks/upload', {
        params: { query: { exam, preview } },
        body: form as unknown as string,
        bodySerializer: (body) => body as unknown as FormData,
      })
      return data ?? failed(error)
    },
    async list(exam) {
      const { data, error } = await client.GET('/admin/mocks', { params: { query: { exam } } })
      return data ?? failed(error)
    },

    async publish(code) {
      const { data, error } = await client.POST('/admin/mocks/{code}/publish', {
        params: { path: { code } },
      })
      return data ?? failed(error)
    },

    async counting(code, towardsDailyLimit) {
      const { data, error } = await client.PUT('/admin/mocks/{code}/counting', {
        params: { path: { code } },
        body: { towardsDailyLimit },
      })
      return data ?? failed(error)
    },

    async discard(code) {
      const { error } = await client.DELETE('/admin/mocks/{code}', { params: { path: { code } } })
      if (error) failed(error)
    },

    async setIntro(code, yaml) {
      const { data, error } = await client.PUT('/admin/mocks/{code}/intro', {
        params: { path: { code } },
        body: { yaml },
      })
      return data ?? failed(error)
    },

    async clearIntro(code) {
      const { data, error } = await client.DELETE('/admin/mocks/{code}/intro', { params: { path: { code } } })
      return data ?? failed(error)
    },

    async preview(code) {
      const { data, error } = await client.POST('/admin/mocks/{code}/preview', { params: { path: { code } } })
      return data ?? failed(error)
    },
  }
}

/** What the API refused with, as an Error the screen can show: its ProblemDetails where it sent one. */
function failed(error: unknown): never {
  const problem = error as { detail?: string; title?: string } | undefined

  throw new Error(problem?.detail ?? problem?.title ?? 'The API refused that.')
}
